/* Attach recommendation placement to the purchased line item.
 * Click-through attribution lasts 30 minutes on the destination product page.
 * No cookies, browser storage, analytics requests or cart-wide attribution.
 */
(() => {
  if (window.hercatUpsellAttribution) return;
  window.hercatUpsellAttribution = true;

  const sources = new Set(['cart_upsell', 'product_page_upsell']);
  const fieldName = 'properties[_upsell_source]';
  const fieldSelector = 'input[data-hercat-upsell-source]';
  const formSelector = 'product-form[data-upsell-product] form';
  const linkSelector = 'a[data-upsell-link], quick-buy-modal a[href]';
  const lifetime = 30 * 60 * 1000;
  let pageAttribution = null;

  function productHandle(url) {
    const match = url.pathname.match(/\/products\/([^/]+)\/?$/);
    try { return match ? decodeURIComponent(match[1]) : ''; }
    catch (_) { return ''; }
  }

  function clearArrivalParameters() {
    const url = new URL(window.location.href);
    url.searchParams.delete('hercat_upsell');
    url.searchParams.delete('hercat_upsell_at');
    window.history.replaceState(window.history.state, '', url.href);
  }

  // A timestamped link carries attribution through navigation and new tabs.
  // Keep Shopify's native recommendation/variant parameters intact.
  const arrival = new URL(window.location.href);
  const arrivalSource = arrival.searchParams.get('hercat_upsell');
  const arrivalTime = Number(arrival.searchParams.get('hercat_upsell_at'));
  const age = Date.now() - arrivalTime;
  if (sources.has(arrivalSource) && productHandle(arrival) && age >= 0 && age < lifetime) {
    pageAttribution = { source: arrivalSource, handle: productHandle(arrival), expires: arrivalTime + lifetime };
  }
  if (!pageAttribution && (arrival.searchParams.has('hercat_upsell') || arrival.searchParams.has('hercat_upsell_at'))) {
    clearArrivalParameters();
  }

  function sourceFor(form) {
    const handle = form.closest('product-form[data-upsell-product]')?.dataset.upsellProduct;
    if (!handle) return '';
    // Prestige moves quick-buy modals to <body>; the modal carries its own source.
    const card = form.closest('quick-buy-modal, product-card, .horizontal-product-card');
    if (card) {
      return card.dataset.upsellProduct === handle && sources.has(card.dataset.upsellSource)
        ? card.dataset.upsellSource : '';
    }
    return pageAttribution?.handle === handle && Date.now() < pageAttribution.expires
      ? pageAttribution.source : '';
  }

  function syncForm(form) {
    const source = sourceFor(form);
    let field = form.querySelector(fieldSelector);
    if (!source) {
      field?.remove();
      return '';
    }
    if (!field) {
      field = document.createElement('input');
      field.type = 'hidden';
      field.name = fieldName;
      field.setAttribute('data-hercat-upsell-source', '');
      form.append(field);
    }
    field.value = source;
    return source;
  }

  function stampLink(link) {
    if (!link?.matches(linkSelector)) return;
    const card = link.closest('[data-upsell-source][data-upsell-product]');
    if (!card || !sources.has(card.dataset.upsellSource)) return;
    const url = new URL(link.href, window.location.href);
    if (url.origin !== window.location.origin || productHandle(url) !== card.dataset.upsellProduct) return;
    url.searchParams.set('hercat_upsell', card.dataset.upsellSource);
    url.searchParams.set('hercat_upsell_at', String(Date.now()));
    link.href = url.href;
  }

  function scan(root) {
    if (root.matches?.(formSelector)) syncForm(root);
    if (root.matches?.(linkSelector)) stampLink(root);
    root.querySelectorAll?.(formSelector).forEach(syncForm);
    root.querySelectorAll?.(linkSelector).forEach(stampLink);
  }

  // Capture before Prestige serializes the form. Existing properties are untouched.
  document.addEventListener('submit', (event) => {
    if (event.target.matches?.(formSelector)) syncForm(event.target);
  }, true);
  document.addEventListener('formdata', (event) => {
    if (!event.target.matches?.(formSelector)) return;
    const source = syncForm(event.target);
    if (source) event.formData.set(fieldName, source);
    else event.formData.delete(fieldName);
  }, true);

  for (const type of ['click', 'auxclick', 'contextmenu']) {
    document.addEventListener(type, (event) => {
      stampLink(event.target.closest?.('a'));
    }, true);
  }

  // Consume a click-through after a successful add, never after a failed attempt.
  document.addEventListener('variant:add', (event) => {
    const handle = event.target.closest?.('product-form[data-upsell-product]')?.dataset.upsellProduct;
    if (pageAttribution?.handle === handle) {
      pageAttribution = null;
      clearArrivalParameters();
      document.querySelectorAll(formSelector).forEach(syncForm);
    }
  });

  function start() {
    scan(document);
    // Cart refreshes and variant-picker rerenders replace forms and cards.
    new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType === 1) scan(node);
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
    if (pageAttribution) {
      window.setTimeout(() => {
        pageAttribution = null;
        clearArrivalParameters();
        document.querySelectorAll(formSelector).forEach(syncForm);
      }, Math.max(0, pageAttribution.expires - Date.now()));
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
